import { useCallback, useRef, useState } from 'react';
import type React from 'react';
import type { ImageStep } from '../components/BettieProfileImageOverlay';

interface UseBettieProfileImageOverlayProps {
  onConfirm?: (payload: { dataUrl: string; originalDataUrl: string }) => void;
}

export const useBettieProfileImageOverlay = ({ onConfirm }: UseBettieProfileImageOverlayProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalImageDataUrlRef = useRef<string | null>(null);

  const [imageStep, setImageStep] = useState<ImageStep>('idle');
  const [selectedImageForCrop, setSelectedImageForCrop] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const openImagePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onChangeFile: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 같은 파일을 다시 선택할 수 있도록 value 초기화
    e.target.value = '';

    originalImageDataUrlRef.current = null;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        originalImageDataUrlRef.current = reader.result;
        setSelectedImageForCrop(reader.result);
        setPreviewImage(null);
        setImageStep('crop');
      }
    };
    reader.onerror = () => {
      console.log('원본 이미지 DataURL 변환 실패');
      originalImageDataUrlRef.current = null;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = useCallback((croppedImageUrl: string) => {
    setSelectedImageForCrop(null);
    setPreviewImage(croppedImageUrl);
    setImageStep('preview');
  }, []);

  const handleBackFromCrop = useCallback(() => {
    setSelectedImageForCrop(null);
    originalImageDataUrlRef.current = null;
    setImageStep('idle');
  }, []);

  const handlePreviewBack = useCallback(() => {
    setPreviewImage(null);
    originalImageDataUrlRef.current = null;
    setImageStep('idle');
  }, []);

  const handlePreviewConfirm = useCallback(async () => {
    if (!previewImage) return;
    try {
      const originalDataUrl = originalImageDataUrlRef.current;
      if (!originalDataUrl) {
        throw new Error('original_data_url이 준비되지 않았습니다.');
      }
      onConfirm?.({ dataUrl: previewImage, originalDataUrl });
      setPreviewImage(null);
      originalImageDataUrlRef.current = null;
      setImageStep('idle');
    } catch (error) {
      console.log(error);
    }
  }, [onConfirm, previewImage]);

  const fileInputProps: React.ComponentProps<'input'> = {
    ref: fileInputRef,
    type: 'file',
    accept: 'image/*',
    className: 'hidden',
    onChange: onChangeFile
  };

  const overlayProps = {
    imageStep,
    selectedImageForCrop,
    previewImage,
    onCropComplete: handleCropComplete,
    onBackFromCrop: handleBackFromCrop,
    onPreviewBack: handlePreviewBack,
    onPreviewConfirm: handlePreviewConfirm
  };

  return {
    openImagePicker,
    fileInputProps,
    overlayProps
  };
};
